import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { renderMessage, Locales } from '@ratehub/base-ui';

import CreditCardShape from '../definitions/CreditCardShape';
import CardCalculationModal from './CardCalculationModal';
import CardCalculationHeader from './CardCalculationHeader';


function CardCalculationModalBalanceTransfer({ cardInfo, isOpen, onClose, ...otherProps }) {
    const intl = useIntl();
    // not using formatPercentage for this to allow 0, 1, or 2 decimal places automatically
    const balanceTransferFeeRateFormatted = intl.formatNumber(cardInfo.balanceTransferFeeRate / 100,
        { style: 'percent', maximumFractionDigits: 2 });

    const balanceLink = intl.locale === Locales.ENGLISH
        ? '/credit-cards/balance-transfer-credit-cards'
        : '/cartes-de-credit/cartes-de-credit-avec-transfert-de-solde';

    return (
        <CardCalculationModal
            isOpen={isOpen}
            onClose={onClose}

            cardName={cardInfo.name}
            cardImageURL={cardInfo.cardImageURL}

            {...otherProps}
        >
            <CardCalculationModal.DefinitionListWrapper>
                <CardCalculationHeader
                    cardName={cardInfo.name}
                    cardImageURL={cardInfo.cardImageURL}
                />

                <CardCalculationModal.ContentGrid>
                    <CardCalculationModal.Description>
                        <FormattedMessage
                            id="cc.results.calculate-how.transferred-balance"
                            defaultMessage="Transferred balance"
                        />
                    </CardCalculationModal.Description>
                    <CardCalculationModal.Value>
                        <output>
                            <FormattedNumber
                                value={cardInfo.balanceTransferAmount}
                                format="CAD0"
                            />
                        </output>
                    </CardCalculationModal.Value>

                    <CardCalculationModal.Description>
                        <FormattedMessage
                            id="cc.results.calculate-how.balance-transfer-promo-rate"
                            defaultMessage="Balance transfer offer rate"
                        />
                    </CardCalculationModal.Description>
                    <CardCalculationModal.Value>
                        <output>
                            <FormattedNumber
                                value={cardInfo.balanceTransferPromoInterest / 100}
                                format="percent2"
                            />
                        </output>
                    </CardCalculationModal.Value>

                    <CardCalculationModal.Description>
                        <FormattedMessage
                            id="cc.results.calculate-how.balance-transfer-offer-duration"
                            defaultMessage="Offer duration"
                        />
                    </CardCalculationModal.Description>
                    <CardCalculationModal.Value>
                        <FormattedMessage
                            id="cc.results.calculate-how.balance-transfer-offer-duration-duration"
                            defaultMessage="{balanceTransferPromoMonths} months"
                            values={{ balanceTransferPromoMonths: cardInfo.balanceTransferPromoMonths }}
                            tagName="output"
                        />
                    </CardCalculationModal.Value>

                    <CardCalculationModal.Separator />

                    <CardCalculationModal.Description
                        className="bringAttention"
                        {...renderMessage({
                            ...MESSAGES.monthlyPayment,
                            values: cardInfo,
                        }, intl)}
                    />
                    <CardCalculationModal.Value>
                        <output className="bringAttention">
                            <FormattedNumber
                                value={cardInfo.balanceTransferMonthlyPayment}
                                format="CAD2"
                            />
                        </output>
                    </CardCalculationModal.Value>

                    <CardCalculationModal.Separator />

                    <CardCalculationModal.Description>
                        <FormattedMessage
                            id="cc.results.calculate-how.annual-fee"
                            defaultMessage="Annual fee"
                        />
                    </CardCalculationModal.Description>
                    <CardCalculationModal.Value>
                        <output>
                            <FormattedNumber
                                value={cardInfo.annualFee}
                                format="CAD0"
                            />
                        </output>
                    </CardCalculationModal.Value>

                    <CardCalculationModal.Description
                        className="bringAttention"
                        {...renderMessage({
                            ...MESSAGES.balanceTransferFee,
                            values: {
                                balanceTransferFeeRateFormatted,
                            },
                        }, intl)}
                    />
                    <CardCalculationModal.Value>
                        <output className="bringAttention">
                            <FormattedNumber
                                value={cardInfo.balanceTransferFee}
                                format={cardInfo.balanceTransferFee % 1 ? 'CAD2' : 'CAD0'}
                            />
                        </output>
                    </CardCalculationModal.Value>

                    <CardCalculationModal.Description
                        className="bringAttention isBottomSummary rh-bg-yuzu-lightest rh-mt-0_5 rh-py-1"
                        {...renderMessage({
                            ...MESSAGES.standardInterestMessage,
                            values: cardInfo,
                        }, intl)}
                    />
                    <CardCalculationModal.Value className="isBottomSummary rh-bg-yuzu-lightest rh-mt-0_5 rh-py-1">
                        <output className="bringAttention">
                            <FormattedNumber
                                value={cardInfo.purchaseInterest / 100}
                                format="percent2"
                            />
                        </output>
                    </CardCalculationModal.Value>
                </CardCalculationModal.ContentGrid>
            </CardCalculationModal.DefinitionListWrapper>

            <p className="rh-text-xs rh-mt-1_5 rh-mb-1_5 rh-mx-1_5">
                <FormattedMessage
                    {...MESSAGES.balanceTransferCalculationDisclaimer}
                    values={{
                        balance: msg =>
                            // eslint-disable-next-line
                            <a className="rh-anchor-animation" href={balanceLink} target="_blank" rel="noopener">
                                {msg}
                            </a>,
                    }}
                />
            </p>
        </CardCalculationModal>
    );
}

CardCalculationModalBalanceTransfer.propTypes = {
    cardInfo: CreditCardShape.isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
};

CardCalculationModalBalanceTransfer.defaultProps = {
    isOpen: true,
    onClose: undefined,
};


const MESSAGES = defineMessages({
    monthlyPayment: {
        id: 'cc.results.calculate-how.monthly-payment',
        defaultMessage: 'Monthly payment{newline}<small>for {balanceTransferPromoMonths} months</small>',
    },

    balanceTransferFee: {
        id: 'cc.results.calculate-how.balance-transfer-fee',
        defaultMessage: 'One time balance transfer fee{newline}<small>{balanceTransferFeeRateFormatted} of transferred balance</small>',
    },

    standardInterestMessage: {
        id: 'cc.results.calculate-how.balance-transfer-standard-interest',
        defaultMessage: 'Standard interest rate after {balanceTransferPromoMonths} months',
    },

    balanceTransferCalculationDisclaimer: {
        id: 'cc.results.calculate-how.balance-transfer-disclaimer',
        defaultMessage: 'This is a simplified example for illustrative purposes. Read more about <balance>how balance transfer works</balance>.',
    },
});


export default observer(CardCalculationModalBalanceTransfer);
